import { UserType } from "@core/enums/user-type";
import { AccountHistoricValue } from "@core/models/account-historic-values.model";
import { AccountRiskProfile } from "@core/models/account-risk-profile.model";
import { CompositionRiskProfile } from "@core/models/composition-risk-profile.model";
import { CompositionVersion } from "@core/models/composition-version.model";
import { Configuration } from "@core/models/configuration.model";
import { DisbursementWithTax } from "@core/models/disbursement-with-tax.model";
import { AccountFund } from "@core/models/fund.model";
import { FundRatioHistory } from "@core/models/fund-ratio-history.model";
import { FundWithRelated } from "@core/models/fund-with-related.model";
import { GeneralAccountCorrection } from "@core/models/general-account-correction.model";
import { GroupedGfhTransaction } from "@core/models/grouped-gfh-transaction.model";
import { RiskProfile } from "@core/models/risk-profile.model";
import { TAllClients } from "@core/models/user.model";
import { CompanyUser } from "@core/models/user-company.model";
import { TPagination } from "@custom-types/sorting";
import { ExtendedGfhAccount } from "@models/extended-gfh-account.model";
import { GfhAccountClosure } from "@models/gfh-account-closure.model";
import { InternalTransfer } from "@models/internal-transfer.model";
import { Quotation } from "@models/quotation.model";
import { PrivateUser } from "@models/user-private.model";
import { WithdrawalAssignment } from "@models/withdrawal-assignment.model";
import { createAction, props } from "@ngrx/store";

export const LOAD_ALL_CLIENTS_PAGINATED = createAction(
  "[Clients And Accounts] Load All Clients Paginated",
  props<{ searchTerm?: string; pagination?: Partial<TPagination> }>()
);
export const LOAD_ALL_CLIENTS = createAction(
  "[Clients And Accounts] Load All Clients",
  props<{ searchTerm?: string }>()
);
export const LOAD_PRIVATE_CLIENTS = createAction(
  "[Clients And Accounts] Load Private Clients",
  props<{ searchTerm?: string; pagination?: Partial<TPagination> }>()
);
export const LOAD_COMPANY_CLIENTS = createAction(
  "[Clients And Accounts] Load Company Clients",
  props<{ searchTerm?: string; pagination?: Partial<TPagination> }>()
);

export const SET_ALL_CLIENTS = createAction(
  "[Clients And Accounts] Set All Clients",
  props<{ clientsList: Array<TAllClients> }>()
);
export const SET_ALL_CLIENTS_PAGINATED = createAction(
  "[Clients And Accounts] Set All Clients Paginated",
  props<{ clientsList: Array<TAllClients> }>()
);
export const SET_PRIVATE_CLIENTS = createAction(
  "[Clients And Accounts] Set Private Clients",
  props<{ clientsList: Array<PrivateUser> }>()
);
export const SET_COMPANY_CLIENTS = createAction(
  "[Clients And Accounts] Set Company Clients",
  props<{ clientsList: Array<CompanyUser> }>()
);

export const SET_CLIENTS_AND_ACCOUNTS_LOADING_STATUS = createAction(
  "[Clients And Accounts] Set Clients And Accounts Loading Status",
  props<{ loading: boolean }>()
);

export const LOAD_CLIENT_ACCOUNTS = createAction(
  "[Clients And Accounts] Load Client Accounts",
  props<{ id: string }>()
);

export const SET_CLIENT_ACCOUNTS = createAction(
  "[Clients And Accounts] Set Client Accounts",
  props<{ accountList: Array<ExtendedGfhAccount> }>()
);

export const SET_CLIENT_ACCOUNTS_TABLE_LOADING_STATUS = createAction(
  "[Clients And Accounts] Set Client Accounts Table Loading Status",
  props<{ loading: boolean }>()
);

export const SAVE_CLIENT = createAction(
  "[Clients And Accounts] Save Client",
  props<{
    client: Partial<PrivateUser | CompanyUser>;
    id: string | undefined;
    userType?: UserType;
    isFirstSave?: boolean;
  }>()
);

export const CHECK_CLIENT_BSN = createAction(
  "[Clients And Accounts] Check Client Bsn",
  props<{ clientBsn: string; countryCode: string }>()
);

export const SET_CLIENT_BSN_VALIDITY = createAction(
  "[Clients And Accounts] Set Client Bsn Validity",
  props<{ valid: boolean }>()
);

export const RECOVER_PASSWORD = createAction(
  "[Clients And Accounts] Recover Password",
  props<{ clientId: string }>()
);

export const SEND_NOTIFICATION_LETTER = createAction(
  "[Clients And Accounts] Send Notification Letter",
  props<{ clientId: string; generalLetter: boolean; sendToCustomer: boolean }>()
);

export const SAVE_ACCOUNT = createAction(
  "[Clients And Accounts] Save Account",
  props<{
    account: Partial<ExtendedGfhAccount>;
    id: string | undefined;
    userType: UserType | undefined;
  }>()
);

export const LOAD_ACCOUNT_DETAILS = createAction(
  "[Clients And Accounts] Load Account Details",
  props<{ id: string }>()
);

export const LOAD_ACCOUNT = createAction(
  "[Clients And Accounts] Load Account",
  props<{ accountNumber: string }>()
);

export const SET_ACCOUNT_DETAILS = createAction(
  "[Clients And Accounts] Set Account Details",
  props<{ account: ExtendedGfhAccount }>()
);

export const LOAD_ACCOUNT_TRANSACTIONS = createAction(
  "[Clients And Accounts] Load Account Transactions",
  props<{ id: string; pagination?: Partial<TPagination> }>()
);

export const SET_ACCOUNT_TRANSACTIONS = createAction(
  "[Clients And Accounts] Set Account Transactions",
  props<{ transactions: Array<GroupedGfhTransaction> }>()
);

export const LOAD_GENERAL_ACCOUNT_CORRECTION_LIST = createAction(
  "[Cilents And Accounts] Load General Account Correction List"
);

export const SET_GENERAL_ACCOUNT_CORRECTION_LIST = createAction(
  "[Cilents And Accounts] Set General Account Correction List",
  props<{ correctionList: Array<GeneralAccountCorrection> }>()
);

export const SET_CORRECTION_LOADING_STATUS = createAction(
  "[Cilents And Accounts] Set Correction Loading Status",
  props<{ loading: boolean }>()
);

export const CREATE_CORRECTION = createAction(
  "[Cilents And Accounts] Create Correction",
  props<{ correction: Partial<GeneralAccountCorrection> }>()
);

export const EXPORT_TRANSACTIONS = createAction(
  "[Clients And Accounts] Export Transactions",
  props<{ startDate: string; endDate: string }>()
);

export const LOAD_ACCOUNT_HISTORIC_VALUE_LIST = createAction(
  "[Clients And Accounts] Load Account Historic Value List",
  props<{ accountId: string; start: number; take: number }>()
);

export const SET_ACCOUNT_HISTORIC_VALUE_LIST = createAction(
  "[Clients And Accounts] Set Account Historic Value List",
  props<{ accountHistoricValueList: Array<AccountHistoricValue> }>()
);

export const SET_FUND_LIST_LOADING_STATUS = createAction(
  "[Clients And Accounts] Set Fund List Loading Status",
  props<{ loading: boolean }>()
);

export const LOAD_FUND_LIST = createAction(
  "[Clients And Accounts] Load Fund List"
);

export const SET_FUND_LIST = createAction(
  "[Clients And Accounts] Set Fund List",
  props<{ fundList: Array<FundWithRelated> }>()
);

export const LOAD_ACCOUNT_CONFIGURATION = createAction(
  "[Clients And Accounts] Load Account Configuration",
  props<{ id?: string }>()
);

export const SET_ACCOUNT_CONFIGURATION = createAction(
  "[Clients And Accounts] Set Account Configuration",
  props<{ configuration: Configuration }>()
);

export const LOAD_WITHDRAWAL_ASSIGNMENT = createAction(
  "[Clients And Accounts] Load Withdrawal Assignment",
  props<{ id: string }>()
);

export const SET_WITHDRAWAL_ASSIGNMENT = createAction(
  "[Clients And Accounts] Set Withdrawal Assignment",
  props<{ withdrawalList: Array<WithdrawalAssignment> }>()
);

export const DELETE_WITHDRAWAL_ASSIGNMENT = createAction(
  "[Clients And Accounts] Delete Withdrawal Assignment",
  props<{ id: string; accountId: string }>()
);

export const GET_TRADE_FOR_ACCOUNT = createAction(
  "[Clients And Accounts] Get Trade For Account",
  props<{ accountId: string }>()
);

export const SAVE_WITHDRAWAL_ASSIGNMENT = createAction(
  "[Clients And Accounts] Save Withdrawal Assignment",
  props<{
    withdrawalAssignment: Partial<WithdrawalAssignment>;
    accountId: string;
  }>()
);

export const UPDATE_ACCOUNT_TRANSACTION = createAction(
  "[Clients And Accounts] Update Account Transaction",
  props<{
    id: string;
    description?: string;
    exchangeInformation?: boolean;
    version: string;
  }>()
);

export const SET_ACCOUNT_TRANSACTIONS_LOADING_STATUS = createAction(
  "[Clients And Accounts] Set Account Transactions Loading Status",
  props<{ loading: boolean }>()
);

export const LOAD_ACCOUNT_DISBURSEMENTS = createAction(
  "[Clients And Accounts] Load Account Disbursements",
  props<{ id: string; pagination?: Partial<TPagination> }>()
);

export const SET_ACCOUNT_DISBURSEMENTS = createAction(
  "[Clients And Accounts] Set Account Disbursements",
  props<{ disbursements: Array<DisbursementWithTax> }>()
);

export const SET_ACCOUNT_DISBURSEMENTS_LOADING_STATUS = createAction(
  "[Clients And Accounts] Set Account Disbursements Loading Status",
  props<{ loading: boolean }>()
);

export const CLOSE_ACCOUNT = createAction(
  "[Clients And Accounts] Close Account",
  props<{ id: string; accountClosingPayload: Partial<GfhAccountClosure> }>()
);

export const SEND_CLOSING_LETTER = createAction(
  "[Clients And Accounts] Send Closing Letter",
  props<{ id: string }>()
);

export const LOAD_PAUSE_REASON_STATUS = createAction(
  "[Clients And Accounts] Load Pause Reason Status"
);

export const SET_PAUSE_REASON_STATUS = createAction(
  "[Clients And Accounts] Set Pause Reason Status",
  props<{ pauseReasonStatus: boolean }>()
);

export const SEND_FRONT_PAGE_LETTER = createAction(
  "[Clients And Accounts] Send Front Page Letter",
  props<{ id: string }>()
);

export const SEND_WELCOME_LETTER = createAction(
  "[Clients And Accounts] Send Welcome Letter",
  props<{ id: string; letter: { sendToClient: boolean } }>()
);

export const LOAD_INTERNAL_TRANSFER_LIST = createAction(
  "[Clients And Accounts] Load Internal Transfer List",
  props<{ accountId: string }>()
);

export const SET_INTERNAL_TRANSFER_LIST = createAction(
  "[Clients And Accounts] Set Internal Transfer List",
  props<{ internalTransferList: Array<InternalTransfer> }>()
);

export const DELETE_INTERNAL_TRANSFER = createAction(
  "[Clients And Accounts] Delete Internal Transfer",
  props<{ id: string; accountId: string }>()
);

export const SAVE_INTERNAL_TRANSFER = createAction(
  "[Clients And Accounts] Save Internal Transfer",
  props<{ internalTransfer: Partial<InternalTransfer>; accountId: string }>()
);

export const LOAD_QUOTATIONS = createAction(
  "[Clients And Accounts] Load Quotations",
  props<{ searchTerm?: string; pagination?: Partial<TPagination> }>()
);

export const SET_QUOTATIONS = createAction(
  "[Clients And Accounts] Set Quotations",
  props<{ quotationList: Array<Quotation> }>()
);

export const DELETE_QUOTATION = createAction(
  "[Clients And Accounts] Delete Quotation",
  props<{ quotationId: string }>()
);

export const LOAD_RISK_PROFILES = createAction(
  "[Clients And Accounts] Load Risk Profiles",
  props<{ id: string }>()
);

export const SET_RISK_PROFILES = createAction(
  "[Clients And Accounts] Set Risk Profiles",
  props<{ riskProfiles: Array<RiskProfile | CompositionRiskProfile> }>()
);

export const LOAD_COMPOSITION_PRODUCT_FUNDS = createAction(
  "[Clients And Accounts] Load Composition Product Funds",
  props<{ id: string }>()
);

export const SET_COMPOSITION_PRODUCT_FUNDS = createAction(
  "[Clients And Accounts] Set Composition Product Funds",
  props<{ compositionFundList: Array<CompositionVersion> }>()
);

export const LOAD_IMPERSONATION_ACCOUNT_REQUEST = createAction(
  "[Clients And Accounts] Load Impersonation Account Request",
  props<{ id: string }>()
);

export const SET_IMPERSONATION_ACCOUNT_REQUEST = createAction(
  "[Clients And Accounts] Set Impersonation Account Request",
  props<{ impersonationAccountRequest: string }>()
);

export const LOAD_ACCOUNT_RISK_PROFILES = createAction(
  "[Clients And Accounts] Load Account Risk Profiles",
  props<{ id: string }>()
);

export const SET_ACCOUNT_RISK_PROFILES = createAction(
  "[Clients And Accounts] Set Account Risk Profiles",
  props<{ accountRiskProfiles: Array<AccountRiskProfile> }>()
);

export const LOAD_FUND_RATIO_HISTORY = createAction(
  "[Clients And Accounts] Load Fund Ratio History",
  props<{ id: string }>()
);

export const SET_FUND_RATIO_HISTORY = createAction(
  "[Clients And Accounts] Set Fund Ratio History",
  props<{ fundRatioHistory: Array<FundRatioHistory> }>()
);

export const REVERSE_CANCEL_ACCOUNT = createAction(
  "[Clients And Accounts] Reverse Cancel Account",
  props<{ id: string }>()
);

export const CHECK_FOR_EXISTING_EMAIL = createAction(
  "[Clients And Accounts] Check For Existing Email",
  props<{ email: string }>()
);

export const SET_EMAIL_VALIDITY = createAction(
  "[Clients And Accounts] Set Email Validity",
  props<{ emailExists: boolean }>()
);

export const SET_CLIENTS_ACCOUNTS_PAGINATION = createAction(
  "[Clients And Accounts] Set Clients Accunts Pagination",
  props<{ pagination: Partial<TPagination> | undefined }>()
);

export const SET_CLIENTS_ACCOUNTS_LIST_LENGTH = createAction(
  "[Clients And Accounts] Set Clients Accunts List Length",
  props<{ length: number }>()
);

export const LOAD_ACCOUNT_FUNDS = createAction(
  "[Clients And Accounts] Load Account Funds",
  props<{ id: string }>()
);

export const SET_ACCOUNT_FUNDS = createAction(
  "[Clients And Accounts] Set Account Funds",
  props<{ accountFundList: Array<AccountFund> }>()
);

export const CHECK_FOR_EXISTING_LEI_NUMBER = createAction(
  "[Clients And Accounts] Check For Existing Lei Number",
  props<{ leiNumber: string }>()
);

export const SET_LEI_NUMBER_VALIDITY = createAction(
  "[Clients And Accounts] Set Lei Number Validity",
  props<{ leiExists: boolean }>()
);

export const CLEAR_CUSTOMERS_AND_ACCOUNTS_STATE = createAction(
  "[Clients And Accounts] Clear Customers and Accounts State"
);
